import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Chip, Stack, ClickAwayListener, ButtonBase } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import Calendar from '../calender/Calendar';
import { colors } from '../../theme/colors';
import StyledTabs from '../Tabs';

const StyledChip = styled(Chip)({
  backgroundColor: colors.secondary,
  color: colors.lightText,
  margin: '4px',
  borderRadius: '4px',
  height: '28px',
  border: `1px solid ${colors.border}`,
  '& .MuiChip-label': {
    fontSize: '12px',
    fontWeight: 400,
    padding: '0 8px',
    whiteSpace: 'nowrap'
  },
  '& .MuiChip-deleteIcon': {
    color: colors.lightText,
    fontSize: '16px',
    '&:hover': {
      color: colors.primary
    }
  }
});

const InputContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#3e3c56',
  color: '#fff',
  border: '1px solid rgba(255, 255, 255, 0.23)',
  borderRadius: '4px',
  padding: '8px 14px',
  width: '93%',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#ff4e3a'
  }
}));

const DateSelector = ({ 
  selectedDates = [], 
  onDateChange,
  placeholder = "Click to select dates",
  isRangeMode = false,
  keepCalendarOpen = true
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [rangeStart, setRangeStart] = useState(null);
  const [pendingSelectedDates, setPendingSelectedDates] = useState(new Set(selectedDates));
  const calendarRef = useRef(null);

  useEffect(() => {
    setPendingSelectedDates(new Set(selectedDates));
  }, [selectedDates]);

  useEffect(() => {
    setRangeStart(null);
    setPendingSelectedDates(new Set(selectedDates));
  }, [isRangeMode, selectedDates]);

  const findDateRanges = (dates) => {
    if (!dates.length) return [];
    
    const sortedDates = [...dates].sort((a, b) => {
      return dayjs(a, 'DD-MM-YYYY').diff(dayjs(b, 'DD-MM-YYYY'));
    });
    
    const ranges = [];
    let currentRange = [sortedDates[0]];

    for (let i = 1; i < sortedDates.length; i++) {
      const currentDate = dayjs(sortedDates[i], 'DD-MM-YYYY');
      const prevDate = dayjs(sortedDates[i-1], 'DD-MM-YYYY');
      const diffDays = currentDate.diff(prevDate, 'day');

      if (diffDays === 1) {
        currentRange.push(sortedDates[i]);
      } else {
        if (currentRange.length >= 3) {
          ranges.push({
            type: 'range',
            dates: [currentRange[0], currentRange[currentRange.length - 1]]
          });
        } else {
          currentRange.forEach(date => {
            ranges.push({
              type: 'single',
              dates: [date]
            });
          });
        }
        currentRange = [sortedDates[i]];
      }
    }

    if (currentRange.length >= 3) {
      ranges.push({
        type: 'range',
        dates: [currentRange[0], currentRange[currentRange.length - 1]]
      });
    } else {
      currentRange.forEach(date => {
        ranges.push({
          type: 'single',
          dates: [date]
        });
      });
    }

    return ranges;
  };

  const renderChips = () => {
    const dateRanges = findDateRanges(selectedDates);

    // --- Determine grid columns based on chip types ---
    const hasRanges = dateRanges.some(range => range.type === 'range');
    const gridColumns = hasRanges ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)';

    return (
      <Box sx={{ 
        display: 'grid',
        // --- Apply dynamic grid columns ---
        gridTemplateColumns: gridColumns, 
        gap: '8px',
        width: '100%'
      }}>
        {dateRanges.map((range, index) => (
          <StyledChip
            key={index}
            label={range.type === 'range' 
              ? `${range.dates[0]} - ${range.dates[1]}`
              : range.dates[0]
            }
            onDelete={() => {
              const datesToRemove = new Set();
              if (range.type === 'range') {
                const start = dayjs(range.dates[0], 'DD-MM-YYYY');
                const end = dayjs(range.dates[1], 'DD-MM-YYYY');
                let current = start.clone();
                while (!current.isAfter(end, 'day')) {
                  datesToRemove.add(current.format('DD-MM-YYYY'));
                  current = current.add(1, 'day');
                }
              } else {
                datesToRemove.add(range.dates[0]);
              }
              const newDates = selectedDates.filter(date => !datesToRemove.has(date));
              onDateChange(newDates);
            }}
          />
        ))}
      </Box>
    );
  };

  const handleClickAway = (event) => {
    if (calendarRef.current && 
        (calendarRef.current.contains(event.target) || 
         event.target.closest('.ant-picker-dropdown') || 
         event.target.closest('.ant-picker-panel') ||
         event.target.closest('.ant-picker-cell') ||
         event.target.closest('.ant-picker'))) {
      return;
    }
    
    if (isCalendarOpen && !isRangeMode) {
      const originalSet = new Set(selectedDates);
      if (pendingSelectedDates.size !== originalSet.size || 
          ![...pendingSelectedDates].every(date => originalSet.has(date))) {
        onDateChange(Array.from(pendingSelectedDates));
      }
    }
    
    setIsCalendarOpen(false);
    setRangeStart(null);
  };

  const openCalendar = () => {
    setPendingSelectedDates(new Set(selectedDates));
    setIsCalendarOpen(true);
  }

  const handleDateClick = (date) => {
    if (!date) return;
    
    const dateStr = date.format('DD-MM-YYYY');
    
    if (isRangeMode) {
      const uniqueDates = [...new Set(selectedDates)];
      if (!rangeStart) {
        setRangeStart(dateStr);
      } else {
        if (dateStr === rangeStart) {
          setRangeStart(null);
        } else {
          const start = dayjs(rangeStart, 'DD-MM-YYYY');
          const end = dayjs(dateStr, 'DD-MM-YYYY');
          const [earlierDate, laterDate] = start.isBefore(end) ? [start, end] : [end, start];
          const dateRangeToAdd = [];
          let current = earlierDate.clone();
          while (!current.isAfter(laterDate, 'day')) {
            dateRangeToAdd.push(current.format('DD-MM-YYYY'));
            current = current.add(1, 'day');
          }
          onDateChange([...new Set([...uniqueDates, ...dateRangeToAdd])]);
          setRangeStart(null);
        }
      }
    } else {
      setPendingSelectedDates(prevPending => {
        const newPending = new Set(prevPending);
        if (newPending.has(dateStr)) {
          newPending.delete(dateStr);
        } else {
          newPending.add(dateStr);
        }
        return newPending;
      });
    }
  };

  const datesForCalendar = isRangeMode ? selectedDates : Array.from(pendingSelectedDates);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative', width: '100%', marginBottom: 2 }}>
        <InputContainer onClick={openCalendar}>
          {selectedDates.length === 0 ? (
            <Box sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>{placeholder}</Box>
          ) : (
            renderChips()
          )}
        </InputContainer>

        {isCalendarOpen && (
          <Box
            ref={calendarRef}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 1,
              zIndex: 1000,
              backgroundColor: '#3e3c56',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}
          >
            <Calendar
              isOpen={true}
              selectedDates={datesForCalendar}
              isRangeSelection={isRangeMode}
              handleDateClick={handleDateClick}
              rangeStart={rangeStart}
            />
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default DateSelector; 